import liff from '@line/liff';
import { useState } from 'react';
import { CustomerActionType, useCustomer } from '../useCustomer';
import {
  createCustomer,
  createGuestCustomer,
  getCustomer,
  getCustomerDoc,
  updateCustomer,
  updateDeauthorizedAt,
} from '../../repositories/customers';
import { useAsync } from '../useAsync';
import {
  getTransactions,
  transRef,
  updateCustomerIds,
} from '../../repositories/transaction';
import { CartActionType, useCart } from '../useCart';
import { getOrders } from '../../repositories/order';
import { getMCusPCards } from '../../repositories/mCusPCard';
import { checkStoreOpen } from './checkStoreOpen';
import {
  Customer,
  StampCardHistory,
  Transaction,
  TransactionFromJson,
} from 'smartdishpro-scripts/ts_common';
import { getPayments } from '../../repositories/payment';
import { dateFromNow, dateToYMD } from '../../utils/date';
import { TransHistory } from '../useHistory/useHistory';
import { useHistory } from 'react-router-dom';
import { route } from '../../router';
import i18next from 'i18next';
import { functions, signInAnonymously } from '../../firebase';

// 1. debug or release　モード
// 2. URL から merchantId、storeId、transactionId を取得
// 3. ログインアカウントの取得（liff）
// 4. 店舗情報の取得
// 5. ユーザ情報の取得
// 6. ポイントカードの閲覧URLの場合は、ポイントカードのページに遷移
// 7. 取引情報の取得(listen)

// 外部ブラウザログイン対応の動作確認方法：
// 1. Chrome DevToolsでsessionStorageを確認:
//    F12 → Application → Session Storage → ドメイン選択 → liffOriginalUrlキーを確認
// 2. Console.logでログイン前後の処理を確認:
//    F12 → Console → "Preserve log"にチェックを入れてリダイレクト前後のログを確認
// 3. 外部ブラウザテスト手順:
//    - 新しいシークレットモードのウィンドウを開く
//    - 完全なURL（transactionID含む）にアクセス
//    - LINEログイン後、元のURLに復元されることを確認

/**
 * ゲストユーザーの初期化処理
 */
const initializeGuestUser = async (merchantId: string, storeId: string) => {
  try {
    // Firebase匿名認証
    await signInAnonymously();
    
    // ゲストユーザー作成
    const guestCustomer = await createGuestCustomer(merchantId, storeId);
    
    return {
      customer: guestCustomer,
      lineId: guestCustomer.id,
      displayName: 'ゲストユーザー',
      pictureUrl: undefined,
      transHistories: [] as TransHistory[]
    };
  } catch (error) {
    throw new Error('ゲストユーザーの作成に失敗しました。もう一度お試しください。');
  }
};

/**
 * LINEユーザーの初期化処理
 */
const initializeLiffUser = async (
  merchantId: string, 
  storeId: string, 
  lineId: string, 
  displayName: string, 
  pictureUrl: string | undefined,
  result: any
) => {
  try {
    // Firestoreからユーザー情報取得/作成
    let customer = (await getCustomer(merchantId, storeId, lineId)) ??
      (await createCustomer(
        merchantId,
        storeId,
        lineId,
        displayName ?? '',
        pictureUrl,
        dateFromNow(result.store.stampExpiredDays),
        result.store.stampExpiredDays === null ? null : dateToYMD(new Date())
      ));

    if (!customer) {
      throw new Error('お客様の情報を確認できませんでした');
    }

    // 取引履歴取得
    let transHistories: TransHistory[] = [];
    try {
      transHistories = await getTransactions(merchantId, storeId, customer.id!);
    } catch (error) {
      console.error('Failed to get transactions:', error);
    }

    // 顧客情報更新処理
    if (customer.pictureUrl !== pictureUrl) {
      customer = { ...customer, pictureUrl: pictureUrl ?? null };
    }
    if (customer.name !== displayName) {
      customer = { ...customer, name: displayName ?? '' };
    }

    // スタンプカード処理（既存のコードをそのまま移動）
    let history: StampCardHistory | undefined;
    if (
      result.store.stampExpiredDays !== null &&
      customer.stampExpiredAt === null
    ) {
      customer = {
        ...customer,
        stampExpiredAt: dateFromNow(result.store.stampExpiredDays),
        stampStartedAt: dateToYMD(new Date()),
      };
    } else if (
      customer.stampStartedAt !== null &&
      customer.stampExpiredAt !== null &&
      customer.stampExpiredAt.getTime() < new Date().getTime()
    ) {
      history = {
        createdAt: new Date(),
        deletedAt: null,
        documentStatus: 'active',
        numOfStamps: transHistories.filter(
          (v) =>
            v.trans.status === 'completeTransaction' ||
            v.trans.status === 'cancelledTransBeforeComplete' ||
            v.trans.status === 'cancelledTransAfterComplete'
        ).length,
        stampExpiredAt: customer.stampExpiredAt,
        stampStartedAt: customer.stampStartedAt,
        updatedAt: new Date(),
      };

      customer = {
        ...customer,
        stampExpiredAt: dateFromNow(result.store.stampExpiredDays),
        stampStartedAt: result.store.stampExpiredDays === null ? null : dateToYMD(new Date()),
      };
    }

    // 顧客情報をFirestoreに更新
    try {
      await updateCustomer(
        merchantId,
        storeId,
        customer.id!,
        customer.name ?? '',
        customer.pictureUrl ?? null,
        customer.stampExpiredAt ?? null,
        customer.stampStartedAt ?? null,
        history
      );
    } catch (error) {
      console.error('Failed to update customer:', error);
    }

    return {
      customer,
      transHistories
    };
  } catch (error) {
    throw new Error('お客様の情報を確認できませんでした もう一度カメラでQRをお読みください');
  }
};

export const useInit = (): {
  init: () => void;
  isLoading: boolean;
  error?: string;
} => {
  /// 言語の切替をする前の読み込みのため、日本語のみ
  const [error, setError] = useState<string | undefined>();

  const { dispatch: customerDispatch } = useCustomer();
  const { dispatch: cartDispatch } = useCart();
  const _useHistory = useHistory();

  const { isLoading, execute: init } = useAsync(async () => {

    // LIFFログイン後のURL復元処理
    // localStorageを使用してタブ間でもURL情報を共有可能にする
    const storageKey = 'liffOriginalUrl';
    const savedData = localStorage.getItem(storageKey);
    const urlParams = new URLSearchParams(window.location.search);

    // 古いデータを自動削除（24時間以上前のデータは削除）
    const cleanupOldData = () => {
      const allKeys = Object.keys(localStorage);
      const now = Date.now();
      allKeys.forEach(key => {
        if (key.startsWith('liffOriginalUrl_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            if (data.timestamp && now - data.timestamp > 24 * 60 * 60 * 1000) {
              localStorage.removeItem(key);
            }
          } catch (e) {
            // 不正なデータは削除
            localStorage.removeItem(key);
          }
        }
      });
    };

    cleanupOldData();

    let savedUrl: string | null = null;
    let savedTimestamp: number | null = null;

    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        savedUrl = parsed.url;
        savedTimestamp = parsed.timestamp;

        // 5分以上前のデータは無効とする（LINEログインのタイムアウト考慮）
        if (savedTimestamp && Date.now() - savedTimestamp > 5 * 60 * 1000) {
          localStorage.removeItem(storageKey);
          savedUrl = null;
        }
      } catch (e) {
        localStorage.removeItem(storageKey);
      }
    }

    // localStorageから元のURLを復元
    if (savedUrl && (urlParams.has('liffClientId') || urlParams.has('code'))) {
      const savedUrlObj = new URL(savedUrl);

      // パスが失われている場合は復元
      if (window.location.pathname !== savedUrlObj.pathname) {
        // localStorageをクリーンアップしてからリダイレクト
        localStorage.removeItem(storageKey);
        window.location.href = savedUrl;
        return;
      } else {
        localStorage.removeItem(storageKey);
      }
    }

    let merchantId: string | undefined;
    let storeId: string | undefined;
    let thirdPath: string | undefined;
    let fourthPath: string | undefined;
    let lineId: string | undefined;
    let displayName: string | undefined;
    let pictureUrl: string | undefined;
    setError(undefined);

    // 1. debug or release　モード

    const isDebug = process.env.REACT_APP_MODE === 'debug';

    // 2. URL から merchantId、storeId、transactionId を取得
    //   merchantId = '0cnNNajvs6zKeJyOT2Gb';
    //   storeId = 'SpbFkQUUY9V6UU729Iv2';
    //   thirdPath = 'c1a98738-235b-422a-8bed-c07dc84fc6ec';
    //   // thirdPath = 'point_cards';
    //   // fourthPath = 'Mo2Xz2Grgb80AUpmMZip';
    //   // thirdPath = 'survey';
    //   // fourthPath = 'KOBzKcpxwsG2zwKsPYeA';
    //   lineId = 'U024ee4168992114bb7c990985fcd7926';
    merchantId = window.location.pathname.split('/')[1];
    storeId = window.location.pathname.split('/')[2];
    thirdPath = window.location.pathname.split('/')[3];
    fourthPath = window.location.pathname.split('/')[4];

    const result = await checkStoreOpen(merchantId, storeId);

    // 3. ログインアカウントの取得（liff）
    if (!result.store?.liffId) {
      setError('正しく読み込めませんでした もう一度カメラでQRをお読みください');
      return;
    }

    // 外部ブラウザでの初回アクセス時、liff.init前にURLを保存
    const isLiffCallback = urlParams.has('liffClientId') || urlParams.has('code');
    if (!isLiffCallback) {
      const dataToSave = {
        url: window.location.href,
        timestamp: Date.now()
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    }
    
    // 🔥 外部ブラウザかつ未ログインの場合はゲスト処理用にLIFF設定変更
    const isExternalBrowser = !liff.isInClient();
    const shouldUseGuestMode = isExternalBrowser;

    try {
      await liff.init({
        liffId: result.store.liffId,
        withLoginOnExternalBrowser: !shouldUseGuestMode,  // 🔥 ゲスト時はfalse
      });
    } catch (initError) {
      setError('LINEログイン情報の取得に失敗しました もう一度お試しください');
      return;
    }

    // deauthorize 対応: 業態変更等でチャネル再同意が必要な店舗の処理
    // 業態変更対応: Store型未定義のためany経由で取得
    if (liff.isLoggedIn() && (result.store as any)?.needsDeauthorize) {
      try {
        const profile = await liff.getProfile();
        const existingCustomer = await getCustomer(merchantId, storeId, profile.userId);

        // 業態変更対応: Customer型未定義のためany経由で取得
        if (existingCustomer?.id && !(existingCustomer as any).deauthorizedAt) {
          const userAccessToken = liff.getAccessToken();
          if (!userAccessToken) {
            throw new Error('LINE access token is not available.');
          }
          await functions.httpsCallable('DeauthorizeUser')({
            userAccessToken,
            merchantId,
            storeId,
            customerId: existingCustomer.id,
          });
          await updateDeauthorizedAt(merchantId, storeId, existingCustomer.id);
          liff.logout();
          setError('店舗の業態を変更したため、右上の × ボタンでアプリを閉じて再度開くか、LINEを再起動してください');
          return;
        }
      } catch (error) {
        console.error('deauthorize failed:', error);
        // 失敗しても通常フローに進む（次回再試行）
      }
    }

    // 🔥 変数のスコープを上に移動
    let customer: Customer | undefined;
    let _transHistories: TransHistory[] = [];

    if (!liff.isLoggedIn() && !liff.isInClient()) {
      // ゲストユーザー作成前に機能制限チェック
      if (thirdPath === 'survey') {
        setError('アンケート機能はLINEログインが必要です');
        return;
      }

      if (thirdPath === 'point_cards') {
        setError('ポイントカード機能はLINEログインが必要です');
        return;
      }

      // ゲストユーザー処理
      const guestResult = await initializeGuestUser(merchantId, storeId);
      customer = guestResult.customer;
      _transHistories = guestResult.transHistories;
      lineId = guestResult.lineId;
      displayName = guestResult.displayName;
      pictureUrl = guestResult.pictureUrl;

      customerDispatch({
        type: CustomerActionType.UPDATE_CUSTOMER,
        payload: { ...customer, registerType: 'guest' },
      });
    }
    else {
      // LINEユーザー処理
      if (liff.isLoggedIn()) {
        try {
          const profile = await liff.getProfile();
          lineId = profile.userId;
          displayName = profile.displayName;
          pictureUrl = profile.pictureUrl;
        } catch (profileError) {
          setError('LINEのプロフィールを取得できませんでした もう一度お試しください');
          return;
        }
      }

      if (!lineId) {
        setError('お客様の情報を確認できませんでした もう一度カメラでQRをお読みください');
        return;
      }

      const lineResult = await initializeLiffUser(merchantId, storeId, lineId, displayName!, pictureUrl, result);
      customer = lineResult.customer;
      _transHistories = lineResult.transHistories;

      customerDispatch({
        type: CustomerActionType.UPDATE_CUSTOMER,
        payload: customer,
      });
    }

    customerDispatch({
      type: CustomerActionType.SET_MERCHNTID,
      payload: merchantId,
    });

    // 5. 店舗情報の取得(営業時間内かどうか)
    if (result?.store === null || result?.store === undefined) {
      setError(
        '店舗の情報を確認できませんでした もう一度カメラでQRをお読みください'
      );
      return;
    } else {
      customerDispatch({
        type: CustomerActionType.SET_STORE,
        payload: result!.store,
      });
    }

    if (result!.store!.isOpen && result.storeHourId) {
      customerDispatch({
        type: CustomerActionType.SET_STOREHOUR_ID,
        payload: result.storeHourId,
      });
    } else {
      setError('こちらの店舗は営業時間外です');
      return;
    }

    customerDispatch({
      type: CustomerActionType.SET_TRANS_HISTORIES,
      payload: _transHistories,
    });

    if (!thirdPath) {
      setError('お客さま専用QRをカメラからお読みください');
      return;
    }

    if (thirdPath === 'point_cards') {
      if (!fourthPath || !customer.mCustomerId) {
        setError('ポイントカード情報を取得する際にエラーが発生しました');
        return;
      }

      const _mCusPCards = await getMCusPCards(merchantId, customer.mCustomerId);
      if (
        !_mCusPCards.some((_card) => _card.value.pointCardId === fourthPath)
      ) {
        _useHistory.push(
          `${route.root}${merchantId}/${storeId}${route.pointCards}/${fourthPath}/create`
        );
      } else {
        _useHistory.push(
          `${route.root}${merchantId}/${storeId}${route.pointCards}/${fourthPath}`
        );
      }

      customerDispatch({
        type: CustomerActionType.SET_MCUSPCARDS,
        payload: _mCusPCards,
      });
      return;
    }

    if (thirdPath === 'survey') {
      if (!fourthPath) {
        setError('アンケート情報を取得する際にエラーが発生しました');
        return;
      }

      _useHistory.push(
        `${route.root}${merchantId}/${storeId}${route.survey}/${fourthPath}`
      );

      return;
    }

    const transId = thirdPath;

    // 6. 取引情報の取得
    transRef(merchantId, storeId)
      .doc(transId)
      .onSnapshot(async (snap) => {
        let trans: Transaction | undefined;
        setError(undefined);

        const data = snap.data({ serverTimestamps: 'estimate' });
        if (snap.exists && data) {
          trans = TransactionFromJson(data, snap.id);
        }

        if (trans) {
          cartDispatch({
            type: CartActionType.SET_TRANS,
            payload: trans,
          });

          const payments = await getPayments(merchantId!, storeId!, trans.id!);
          cartDispatch({
            type: CartActionType.SET_PAYMENTS,
            payload: payments,
          });
        } else {
          setError(
            'テーブルの情報を確認できませんでした もう一度カメラでQRをお読みください'
          );
          return;
        }

        if (trans?.status !== 'underTransaction') {
          setError(
            'ご来店ありがとうございました またのご来店を、心よりお待ちしております'
          );
          return;
        }

        const orders = await getOrders(merchantId!, storeId!, transId);
        cartDispatch({
          type: CartActionType.SET_ORDERS,
          payload: orders,
        });

        let customers: Customer[] = [customer!];
        const ids = trans?.customerIds.filter((_id) => customer?.id !== _id);
        for (let index = 0; index < ids.length; index++) {
          try {
            const _customer = await getCustomerDoc(
              merchantId!,
              storeId!,
              ids[index]
            );
            if (_customer) {
              customers.push(_customer);
            }
          }
          catch (error) {
            console.error('Failed to get customer document:', error);
          }
        }

        cartDispatch({
          type: CartActionType.SET_CUSTOMERS,
          payload: customers,
        });

        if (!trans?.customerIds.includes(customer!.id!)) {
          if (
            !_transHistories.some((_history) => _history.trans.id === trans?.id)
          ) {
            customerDispatch({
              type: CustomerActionType.SET_TRANS_HISTORIES,
              payload: _transHistories.concat({ trans: trans, orders: [] }),
            });
          }

          // 🔥 LINEユーザーの場合のみスタンプカード処理
          if (liff.isLoggedIn() && result!.store?.stampFunctionInUse === true) {
            // QRの初回読み込み時に、スタンプカードを表示させるため
            customerDispatch({
              type: CustomerActionType.SET_CHECKEDIN,
              payload: false,
            });
          }

          if (merchantId === 'SpbFkQUUY9V6UU729Ivk') {
            // QRの初回読み込み時に、アルコールの確認
            await i18next.changeLanguage('ja');
            customerDispatch({
              type: CustomerActionType.SET_CHECKEDIN_ALCHOL,
              payload: false,
            });
          }

          await updateCustomerIds(
            merchantId!,
            storeId!,
            transId,
            customer!.id!
          );

          // 🔥 LINEユーザーの場合のみサービスメッセージ送信
          if (!isDebug && liff.isLoggedIn()) {
            const userAToken = liff.getAccessToken();
            await functions.httpsCallable('LineSendServiceMessage').call(
              {},
              {
                merchantId: merchantId,
                storeId: storeId,
                tableOrderUrl: trans.tableOrderUrl,
                userAToken: userAToken,
              }
            );
          }
        }
      });

    _useHistory.push(
      `${route.root}${merchantId}/${storeId}/${transId}${route.items}`
    );
  });

  return { init, isLoading, error };
};
